import React, {useCallback, useMemo} from 'react'
import {useIntl} from 'react-intl'
import {useSelector} from 'react-redux'
import {filterMessages} from 'components/Filters/filterMessages'
import {getPriceActiveLabel} from 'components/Filters/utils'
import {getIsFilterSelected} from 'components/Filters/utils'
import {getCurrencyCode} from 'modules/meta/selectors'
import {getNeighborhoods, getPropertyBrands} from 'modules/sapiSearch/selectors'
import {getListOfValues} from 'modules/search/selectors'
import {FilterUrlParams} from 'modules/search/types'
import {PROPERTY_TYPE_HOTEL_FACET_IDS} from 'utils/facilities'
import {toArray} from 'utils/object'

import {Icon} from '@vio/atlas/Icon'
import {useBrand} from '@vio/core/src/_web/brand/hooks/useBrand'
import {useBrandCapabilities} from '@vio/core/src/_web/brand/hooks/useBrandCapabilities'
import {
  getReviewSentimentKey,
  reviewSentimentMessages
} from '@vio/core/src/hotel/reviewSentiments'
import {getEnabledCurrencies} from '@vio/core/src/localization/business/currency'

import {usePriceFilter} from './usePriceFilter'

interface FilterDescription {
  id: string
  isSelected: boolean
  label: string
  iconStart?: React.ReactElement
  change:
    | {key: keyof FilterUrlParams; value: string}
    | {filters: FilterUrlParams}
}

export const useFilterDescriptions = ({
  filters,
  appliedFilters = filters
}: {
  filters: FilterUrlParams
  appliedFilters?: FilterUrlParams
}) => {
  const {formatMessage} = useIntl()
  const {brandName} = useBrand()
  const {
    monetisation: {onlyOnBrandTag}
  } = useBrandCapabilities()
  const {getTotalPriceFilter} = usePriceFilter()
  const listOfValues = useSelector(getListOfValues)

  const currencyCode = useSelector(getCurrencyCode)
  const neighborhoods = useSelector(getNeighborhoods)
  const propertyBrands = useSelector(getPropertyBrands)

  const priceCurrencySymbol = useMemo(() => {
    const enabledCurrencies = getEnabledCurrencies(
      process.env.NEXT_PUBLIC_ENABLED_CURRENCIES
    )
    const currency = enabledCurrencies.find(
      currency => currency.code === currencyCode
    )
    return currency?.symbol ?? ''
  }, [currencyCode])

  const getNeighborhoodName = useCallback(
    (neighborhoodId: string) => {
      return (
        neighborhoods?.find(neighborhood => neighborhood.id === neighborhoodId)
          ?.value ?? ''
      )
    },
    [neighborhoods]
  )
  const getPropertyBrandName = useCallback(
    (propertyBrandId: string) => {
      return (
        propertyBrands?.find(
          propertyBrand => propertyBrand.id === propertyBrandId
        )?.value ?? ''
      )
    },
    [propertyBrands]
  )

  const getFacilities = useCallback((): FilterDescription[] => {
    const allFacilities = new Set([
      ...toArray(filters?.facilities),
      ...toArray(appliedFilters?.facilities)
    ])

    if (allFacilities.size === 0) return []

    return [...allFacilities].map(facetId => ({
      id: `Facility:${facetId}`,
      isSelected: getIsFilterSelected(filters?.facilities || [], facetId),
      iconStart: undefined,
      change: {key: 'facilities', value: facetId},
      label: listOfValues?.[`Facility:${facetId}`]?.value?.toString() ?? ''
    }))
  }, [filters, listOfValues, appliedFilters])

  const getThemes = useCallback((): FilterDescription[] => {
    const allThemes = new Set([
      ...toArray(filters?.themes),
      ...toArray(appliedFilters?.themes)
    ])

    if (allThemes.size === 0) return []

    return [...allThemes].map(themeId => ({
      id: `Theme:${themeId}`,
      isSelected: getIsFilterSelected(filters?.themes, themeId),
      iconStart: undefined,
      change: {key: 'themes', value: themeId},
      label: listOfValues?.[`Theme:${themeId}`]?.value?.toString() ?? ''
    }))
  }, [filters, listOfValues, appliedFilters])

  const getPropertyTypes = useCallback((): FilterDescription[] => {
    const allPropertyTypes = new Set([
      ...toArray(filters?.propertyTypes),
      ...toArray(appliedFilters?.propertyTypes)
    ])

    // hotels property types should be displayed first
    const propertyTypesListSorted = [...allPropertyTypes].sort(a =>
      PROPERTY_TYPE_HOTEL_FACET_IDS.has(a) ? -1 : 0
    )

    if (allPropertyTypes.size === 0) return []

    return propertyTypesListSorted?.map(propertyId => ({
      id: `PropertyType:${propertyId}`,
      isSelected: getIsFilterSelected(filters?.propertyTypes, propertyId),
      iconStart: undefined,
      change: {key: 'propertyTypes', value: propertyId},
      label:
        listOfValues?.[`PropertyType:${propertyId}`]?.value?.toString() ?? ''
    }))
  }, [filters, listOfValues, appliedFilters])

  const getStarRatings = useCallback((): FilterDescription[] => {
    const allStarRatings = new Set([
      ...toArray(filters?.starRatings),
      ...toArray(appliedFilters?.starRatings)
    ])

    if (allStarRatings.size === 0) return []

    return [...allStarRatings].map(starRating => {
      const isSelected = getIsFilterSelected(
        filters?.starRatings ?? [],
        starRating
      )
      return {
        id: `StarRatings:${starRating}`,
        isSelected,
        change: {key: 'starRatings', value: starRating},
        iconStart: <Icon name={'Star'} />,
        label: formatMessage(
          {
            id: 'filters.starRating',
            defaultMessage: '{starRating, plural, one {1‑star} other {#‑star}}'
          },
          {starRating}
        )
      }
    })
  }, [filters, appliedFilters, formatMessage])

  const getFreeCancellation = useCallback((): FilterDescription[] => {
    const isSelected = filters?.freeCancellation === '1'

    if (
      appliedFilters?.freeCancellation !== '1' &&
      filters?.freeCancellation !== '1'
    )
      return []

    return [
      {
        id: 'FreeCancellation',
        isSelected,
        iconStart: undefined,
        change: {filters: {freeCancellation: isSelected ? undefined : '1'}},
        label: formatMessage(filterMessages.freeCancellationTitle)
      }
    ]
  }, [filters, appliedFilters, formatMessage])

  const getPayLater = useCallback((): FilterDescription[] => {
    const isSelected = filters?.payLater === '1'
    if (appliedFilters?.payLater !== '1' && filters?.payLater !== '1') return []

    return [
      {
        id: 'PayLater',
        isSelected,
        iconStart: undefined,
        change: {filters: {payLater: isSelected ? undefined : '1'}},
        label: formatMessage(filterMessages.payLater)
      }
    ]
  }, [filters, appliedFilters, formatMessage])

  const getExclusive = useCallback((): FilterDescription[] => {
    const isSelected = filters?.exclusive === '1'
    if (appliedFilters?.exclusive !== '1' && filters?.exclusive !== '1')
      return []
    if (!onlyOnBrandTag.enabled) return []

    return [
      {
        id: 'Exclusive',
        isSelected,
        iconStart: undefined,
        change: {filters: {exclusive: isSelected ? undefined : '1'}},
        label: formatMessage(onlyOnBrandTag.config.message, {brandName})
      }
    ]
  }, [filters, appliedFilters, formatMessage, onlyOnBrandTag, brandName])

  const getReviewScore = useCallback((): FilterDescription[] => {
    const isSelected = Boolean(filters?.reviewScore)
    if (!appliedFilters?.reviewScore && !filters?.reviewScore) return []

    const sentimentMessageKey = getReviewSentimentKey(
      Number(filters?.reviewScore ?? appliedFilters?.reviewScore)
    )
    const translatedSentiment = sentimentMessageKey
      ? reviewSentimentMessages[sentimentMessageKey]
      : null

    return [
      {
        id: 'ReviewScore',
        isSelected,
        iconStart: undefined,
        change: {
          filters: {
            reviewScore: isSelected ? undefined : appliedFilters?.reviewScore
          }
        },
        label: `${formatMessage(translatedSentiment)} ${filters?.reviewScore ?? appliedFilters?.reviewScore}+`
      }
    ]
  }, [filters, appliedFilters, formatMessage])

  const getPrice = useCallback((): FilterDescription[] => {
    const isSelected = Boolean(filters?.priceMin || filters?.priceMax)

    if (
      !appliedFilters?.priceMin &&
      !appliedFilters?.priceMax &&
      !filters?.priceMin &&
      !filters?.priceMax
    )
      return []

    return [
      {
        id: 'Price',
        isSelected,
        iconStart: undefined,
        change: {
          filters: {
            priceMin: isSelected ? undefined : appliedFilters?.priceMin,
            priceMax: isSelected ? undefined : appliedFilters?.priceMax
          }
        },
        label: getPriceActiveLabel({
          minPrice: getTotalPriceFilter(
            isSelected ? filters.priceMin : appliedFilters?.priceMin
          ),
          maxPrice: getTotalPriceFilter(
            isSelected ? filters.priceMax : appliedFilters?.priceMax
          ),
          currencySymbol: priceCurrencySymbol,
          formatMessage
        })
      }
    ]
  }, [
    filters,
    appliedFilters,
    priceCurrencySymbol,
    formatMessage,
    getTotalPriceFilter
  ])

  const getMeals = useCallback((): FilterDescription[] => {
    const allMeals = new Set([
      ...toArray(filters?.amenities),
      ...toArray(appliedFilters?.amenities)
    ])

    if (allMeals.size === 0) return []

    return [...allMeals].map(mealId => ({
      id: `Amenities:${mealId}`,
      isSelected: getIsFilterSelected(filters?.amenities, mealId),
      iconStart: undefined,
      change: {key: 'amenities', value: mealId},
      label: formatMessage(filterMessages[mealId])
    }))
  }, [filters, appliedFilters, formatMessage])

  const getNeighborhoodsFilters = useCallback((): FilterDescription[] => {
    const allNeighborhoods = new Set([
      ...toArray(filters?.neighborhoodIds),
      ...toArray(appliedFilters?.neighborhoodIds)
    ])
    if (allNeighborhoods.size === 0) return []
    return [...allNeighborhoods].map(neighborhoodId => ({
      id: `Neighborhood:${neighborhoodId}`,
      isSelected: getIsFilterSelected(filters?.neighborhoodIds, neighborhoodId),
      iconStart: undefined,
      change: {key: 'neighborhoodIds', value: neighborhoodId},
      label: getNeighborhoodName(neighborhoodId)
    }))
  }, [filters, appliedFilters, getNeighborhoodName])

  const getPropertyBrandsFilters = useCallback((): FilterDescription[] => {
    const allPropertyBrands = new Set([
      ...toArray(filters?.brandIds),
      ...toArray(appliedFilters?.brandIds)
    ])
    if (allPropertyBrands.size === 0) return []
    return [...allPropertyBrands].map(propertyBrandId => ({
      id: `PropertyBrand:${propertyBrandId}`,
      isSelected: getIsFilterSelected(filters?.brandIds, propertyBrandId),
      iconStart: undefined,
      change: {key: 'brandIds', value: propertyBrandId},
      label: getPropertyBrandName(propertyBrandId)
    }))
  }, [filters, appliedFilters, getPropertyBrandName])

  // Added as a part of c747927f-deal-freeze-srp
  const getDealFreeze = useCallback((): FilterDescription[] => {
    const isSelected = filters?.dealFreeze === '1'

    if (appliedFilters?.dealFreeze !== '1' && filters?.dealFreeze !== '1')
      return []

    return [
      {
        id: 'Freezable',
        isSelected,
        iconStart: undefined,
        change: {filters: {dealFreeze: isSelected ? undefined : '1'}},
        label: formatMessage(filterMessages.freezable)
      }
    ]
  }, [filters, appliedFilters, formatMessage])

  const selectedFiltersList: FilterDescription[] = useMemo(() => {
    const filters = [
      ...getFacilities(),
      ...getThemes(),
      ...getPropertyTypes(),
      ...getStarRatings(),
      ...getFreeCancellation(),
      ...getPayLater(),
      ...getReviewScore(),
      ...getPrice(),
      ...getMeals(),
      ...getNeighborhoodsFilters(),
      ...getPropertyBrandsFilters(),
      ...getExclusive(),
      ...getDealFreeze()
    ]
    return filters.sort((a, b) => {
      if (a.isSelected !== b.isSelected) {
        return a.isSelected ? -1 : 1
      }
    })
  }, [
    getFacilities,
    getThemes,
    getPropertyTypes,
    getStarRatings,
    getFreeCancellation,
    getPayLater,
    getReviewScore,
    getPrice,
    getMeals,
    getNeighborhoodsFilters,
    getPropertyBrandsFilters,
    getExclusive,
    getDealFreeze
  ])

  return selectedFiltersList
}
